/**
 * Mannequin bodies for the ward. The two rigged Mixamo bodies are posed once with the idle
 * clip and baked to static geometry of unit height, so every subject is an instance of one
 * of six meshes (two bodies, three LODs) instead of a skinned mesh of its own. A small
 * vertex-shader breathing and weight-shift animation with a per-instance phase keeps them
 * alive without a skeleton per subject.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

/** Darkening baked into the vertex colour of the joint shells so the body reads as one tone. */
const JOINT_TINT = 0.62;

export interface BakedBody {
  /** Unit height, feet on y=0, centred on x and z, facing +z. */
  geometry: THREE.BufferGeometry;
  /** The rig's own standing height in metres before normalisation. */
  height: number;
  triangles: number;
}

/**
 * Poses the skinned meshes under `root` (optionally with a clip at `time` seconds) and bakes
 * them into one static geometry. Secondary shells (joints) are darkened through vertex colour.
 */
export function bakeBody(root: THREE.Object3D, clip?: THREE.AnimationClip, time = 0): BakedBody {
  if (clip) {
    const mixer = new THREE.AnimationMixer(root);
    mixer.clipAction(clip).play();
    mixer.update(time);
  }
  root.updateMatrixWorld(true);
  const parts: THREE.BufferGeometry[] = [];
  const v = new THREE.Vector3();
  const skinned: THREE.SkinnedMesh[] = [];
  root.traverse((o) => {
    if ((o as THREE.SkinnedMesh).isSkinnedMesh) skinned.push(o as THREE.SkinnedMesh);
  });
  // The larger shell is the surface; anything else is a joint shell and gets darkened.
  const surface = skinned.reduce<THREE.SkinnedMesh | undefined>(
    (best, m) =>
      !best ||
      m.geometry.getAttribute('position').count > best.geometry.getAttribute('position').count
        ? m
        : best,
    undefined,
  );
  for (const mesh of skinned) {
    const source = mesh.geometry.getAttribute('position');
    const n = source.count;
    const positions = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      v.fromBufferAttribute(source, i);
      mesh.applyBoneTransform(i, v);
      v.applyMatrix4(mesh.matrixWorld);
      positions[i * 3] = v.x;
      positions[i * 3 + 1] = v.y;
      positions[i * 3 + 2] = v.z;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const tint = mesh === surface ? 1 : JOINT_TINT;
    g.setAttribute('color', new THREE.BufferAttribute(new Float32Array(n * 3).fill(tint), 3));
    if (mesh.geometry.index) g.setIndex(mesh.geometry.index.clone());
    parts.push(g);
  }
  let geometry: THREE.BufferGeometry;
  if (parts.length === 0) geometry = new THREE.BufferGeometry();
  else if (parts.length === 1) geometry = parts[0]!;
  else {
    const indexed = parts.every((p) => p.index);
    geometry = mergeGeometries(indexed ? parts : parts.map((p) => p.toNonIndexed()), false);
  }
  geometry.computeBoundingBox();
  const box = geometry.boundingBox ?? new THREE.Box3();
  const height = Math.max(1e-3, box.max.y - box.min.y);
  geometry.translate(-(box.min.x + box.max.x) / 2, -box.min.y, -(box.min.z + box.max.z) / 2);
  geometry.scale(1 / height, 1 / height, 1 / height);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  const triangles = geometry.index
    ? geometry.index.count / 3
    : geometry.getAttribute('position').count / 3;
  return { geometry, height, triangles };
}

export interface MannequinMaterial extends THREE.MeshStandardMaterial {
  uniforms: { uTime: { value: number } };
}

/**
 * Matte body material with the breathing / weight-shift animation. The geometry is unit
 * height, so the chest band and sway are expressed in body fractions; `phase` is a
 * per-instance attribute so the ward does not breathe in unison.
 */
export function mannequinMaterial(): MannequinMaterial {
  const material = new THREE.MeshStandardMaterial({
    roughness: 0.85,
    metalness: 0,
    vertexColors: true,
    emissive: 0xffffff,
    emissiveIntensity: 0.07,
  }) as MannequinMaterial;
  material.uniforms = { uTime: { value: 0 } };
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uTime = material.uniforms.uTime;
    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        '#include <common>\nattribute float phase;\nuniform float uTime;',
      )
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
        float breath = sin(uTime * 1.5 + phase);
        float chest = smoothstep(0.5, 0.68, transformed.y) * (1.0 - smoothstep(0.7, 0.9, transformed.y));
        transformed.xz *= 1.0 + 0.016 * breath * chest;
        transformed.y += 0.003 * breath * smoothstep(0.45, 0.9, transformed.y);
        float sway = sin(uTime * 0.4 + phase * 1.7);
        transformed.x += 0.012 * sway * smoothstep(0.0, 0.95, transformed.y);`,
      );
  };
  material.customProgramCacheKey = () => 'ward-mannequin';
  return material;
}

/** Adds the per-instance phase attribute an instanced body needs for the shader. */
export function withPhases(geometry: THREE.BufferGeometry, capacity: number): THREE.BufferGeometry {
  const phases = new Float32Array(capacity);
  for (let i = 0; i < capacity; i++) phases[i] = (i * 2.399) % (Math.PI * 2);
  geometry.setAttribute('phase', new THREE.InstancedBufferAttribute(phases, 1));
  return geometry;
}
